// src/services/AnalyticsService.js

// Helper to ensure dataLayer exists
const initDataLayer = () => {
    window.dataLayer = window.dataLayer || [];
};

/**
 * Analytics Service for tracking question-focused AI interactions
 */
const AnalyticsService = {
    /**
     * Track when a survey is started
     */
    trackSurveyStart: (surveyId, surveyTitle, questionCount) => {
        initDataLayer();
        window.dataLayer.push({
            event: 'survey_start',
            surveyId,
            surveyTitle,
            questionCount,
            timestamp: new Date().toISOString()
        });
    },

    /**
     * Track when a question is displayed to the user
     */
    trackQuestionView: (surveyId, questionId, questionText, questionIndex, isFollowUp) => {
        initDataLayer();
        window.dataLayer.push({
            event: 'question_view',
            surveyId,
            questionId,
            questionText,
            questionIndex,
            isFollowUp: isFollowUp || false,
            timestamp: new Date().toISOString()
        });
    },

    /**
     * Track when a user responds to a question
     */
    trackQuestionResponse: (surveyId, questionId, questionIndex, responseLength, responseTime, isFollowUpResponse) => {
        initDataLayer();
        window.dataLayer.push({
            event: 'question_response',
            surveyId,
            questionId,
            questionIndex,
            responseLength,
            responseTime, // in milliseconds
            isFollowUpResponse: isFollowUpResponse || false,
            timestamp: new Date().toISOString()
        });
    },

    /**
     * Track when a user skips a question
     */
    trackQuestionSkipped: (surveyId, questionId, questionIndex) => {
        initDataLayer();
        window.dataLayer.push({
            event: 'question_skipped',
            surveyId,
            questionId,
            questionIndex,
            timestamp: new Date().toISOString()
        });
    },

    /**
     * Track when an AI generates a follow-up question
     */
    trackFollowUpGeneration: (surveyId, baseQuestionId, questionIndex, followUpQuestion, followUpCount, generationTime) => {
        initDataLayer();
        window.dataLayer.push({
            event: 'followup_generation',
            surveyId,
            baseQuestionId,
            questionIndex,
            followUpQuestion,
            followUpCount,
            generationTime, // in milliseconds
            timestamp: new Date().toISOString()
        });
    },

    /**
     * Track AI's assessment of a response
     */
    trackResponseAssessment: (surveyId, questionId, questionIndex, assessment, reason) => {
        initDataLayer();
        window.dataLayer.push({
            event: 'response_assessment',
            surveyId,
            questionId,
            questionIndex,
            assessment, // "low", "medium", "high"
            reason,
            timestamp: new Date().toISOString()
        });
    },

    /**
     * Track survey completion
     */
    trackSurveyCompletion: (surveyId, totalQuestions, answeredQuestions, totalFollowUps, totalTime) => {
        initDataLayer();
        window.dataLayer.push({
            event: 'survey_completion',
            surveyId,
            totalQuestions,
            answeredQuestions,
            totalFollowUps,
            totalTime, // in milliseconds
            completionRate: (answeredQuestions / totalQuestions) * 100,
            timestamp: new Date().toISOString()
        });
    },

    /**
     * Track survey abandonment
     */
    trackSurveyAbandonment: (surveyId, lastQuestionIndex, totalQuestions, answeredQuestions, timeSpent) => {
        initDataLayer();
        window.dataLayer.push({
            event: 'survey_abandonment',
            surveyId,
            lastQuestionIndex,
            totalQuestions,
            answeredQuestions,
            timeSpent, // in milliseconds
            completionRate: (answeredQuestions / totalQuestions) * 100,
            timestamp: new Date().toISOString()
        });
    },

    /**
     * Track when a user requests a transcript email
     */
    trackTranscriptRequest: (surveyId, email, messageCount) => {
        initDataLayer();

        // Hash the email for privacy
        let hashedEmail = '';
        if (email) {
            // Simple hash function
            let hash = 0;
            for (let i = 0; i < email.length; i++) {
                hash = ((hash << 5) - hash) + email.charCodeAt(i);
                hash |= 0; // Convert to 32bit integer
            }
            hashedEmail = hash.toString(36);
        }

        window.dataLayer.push({
            event: 'transcript_request',
            surveyId,
            hashedEmail,
            messageCount,
            timestamp: new Date().toISOString()
        });
    }
};

export default AnalyticsService;